/** Live view of one open trade — the "cockpit" during a running position.
 *
 * Displays the fields the operator needs at a glance:
 *  - Contract symbol + bias
 *  - Entry premium (fill-based) → Current SL (moves as trail fires)
 *  - Peak seen (HWM), so operator can eyeball how close to next ratchet
 *  - Trail count (increments with each Telegram "Trail step N")
 *  - Time held (updates every render tick from parent's ticking clock)
 *  - Optional link to the strategy detail page
 *
 * Consumer must own the ticking clock (`now` prop) so the "Held" display
 * updates without this component managing its own timer. */

import { useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { ContractLink } from "@/components/ui/ContractLink";
import type { TradeLogEntry } from "@/types/tradeLog";
import { entryTile, formatPrice, hwmHint, slHint, slTile, targetTile } from "./levels";
import { rungOf } from "@/lib/strikeRung";
import { LiveStrip } from "./LiveStrip";
import { QueuedExitBanner } from "./QueuedExitBanner";

interface Props {
  trade: TradeLogEntry;
  now: Date;
  /** When true, adds a "Open in strategy →" link under the card. */
  showStrategyLink?: boolean;
  /** When provided, renders a Force-exit button that calls this handler
   * after a two-step confirm. Omit for read-only views (tests, snapshots). */
  onForceExit?: (tradeId: string) => Promise<void>;
  /** Live option LTP from the section's 3s poll. Omit/null hides the
   * live strip (tests, broker feed unavailable at first render). */
  liveLtp?: number | null;
  /** True when the LTP poll is erroring — the strip flags the shown
   * price as stale instead of pretending it's live. */
  liveLtpStale?: boolean;
  /** Live INDEX spot for this trade's underlying, from the section's own
   * poll. Shown beside the premium so the option price can be read against
   * the index that drives it (operator, 2026-09-29). Null hides it — the
   * strip never invents a spot from the premium. */
  liveSpot?: number | null;
}

export function OpenPositionCard({
  trade,
  now,
  showStrategyLink = false,
  onForceExit,
  liveLtp = null,
  liveLtpStale = false,
  liveSpot = null,
}: Readonly<Props>): ReactElement {
  const held = formatHeld(trade.entryAt, now);
  return (
    <section className="card p-4">
      <CardHeader trade={trade} held={held} />
      <LiveStrip trade={trade} ltp={liveLtp} stale={liveLtpStale} spot={liveSpot} />
      <StatGrid trade={trade} />
      <QueuedExitBanner queuedExitAt={trade.queuedExitAt} />
      <CardFooter
        strategyId={trade.strategyId}
        tradeId={trade.id}
        showStrategyLink={showStrategyLink}
        onForceExit={onForceExit}
      />
    </section>
  );
}

function CardHeader({
  trade,
  held,
}: Readonly<{ trade: TradeLogEntry; held: string }>): ReactElement {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-secondary">Open position</div>
        <div className="mt-1 flex items-baseline gap-2">
          <ContractLink symbol={trade.contractSymbol} className="text-sm font-medium" />
          <BiasBadge bias={trade.bias} />
          <RungBadge trade={trade} />
          {trade.executionMode === "PAPER" && (
            <span className="pill bg-surface-2 text-ink-secondary">
              <i className="ti ti-flask text-xs" /> PAPER
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-ink-secondary">
          {trade.strategyName} · entered {formatEntryTime(trade.entryAt)} · held {held}
          {trade.quantity !== null && ` · qty ${String(trade.quantity)}${lotsLabel(trade.lots)}`}
        </div>
      </div>
      <TrailBadge count={trade.trailCount} />
    </div>
  );
}

function StatGrid({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement {
  // The ladder tiles describe the TRAIL exit and are meaningful only when
  // this trade carries trail params. A bias trade under the fixed-stop +
  // swing-target style (2026-08-06) has neither trigger nor amount, so the
  // old card showed "next SL / next ratchet" steps that could never fire
  // (operator, 2026-08-07) while hiding the target that actually exits it.
  const hasLadder = trade.trailTriggerPoints !== null && trade.trailAmountPoints !== null;
  if (!hasLadder) {
    const sl = slTile(trade);
    const target = targetTile(trade);
    const entry = entryTile(trade);
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <MiniStat label={entry.label} value={entry.value} hint={entry.hint} />
        <MiniStat label={sl.label} value={sl.value} hint={sl.hint} />
        <MiniStat label={target.label} value={target.value} hint={target.hint} />
      </div>
    );
  }
  const next = computeNextThresholdDistance(trade);
  const entry = entryTile(trade);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <MiniStat label={entry.label} value={entry.value} hint={entry.hint} />
      <MiniStat label="SL" value={formatPrice(trade.currentSlPrice)} hint={slHint(trade)} />
      <MiniStat label="Peak seen" value={formatPrice(trade.highWaterMark)} hint={hwmHint(trade)} />
      <MiniStat label="Next SL" value={nextSlLabel(trade)} hint={nextSlHint(trade)} />
      <MiniStat label="Next ratchet at" value={next.thresholdLabel} hint={next.distanceLabel} />
    </div>
  );
}

function CardFooter({
  strategyId,
  tradeId,
  showStrategyLink,
  onForceExit,
}: Readonly<{
  strategyId: string;
  tradeId: string;
  showStrategyLink: boolean;
  onForceExit: ((tradeId: string) => Promise<void>) | undefined;
}>): ReactElement | null {
  if (!showStrategyLink && !onForceExit) return null;
  return (
    <div className="mt-3 flex items-center justify-between gap-3">
      {showStrategyLink ? <StrategyLink strategyId={strategyId} /> : <span />}
      {onForceExit && <ForceExitButton tradeId={tradeId} onConfirm={onForceExit} />}
    </div>
  );
}

function StrategyLink({ strategyId }: Readonly<{ strategyId: string }>): ReactElement {
  return (
    <Link
      to={`/strategies/${strategyId}`}
      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
    >
      Open strategy detail
      <i className="ti ti-chevron-right text-xs" />
    </Link>
  );
}

type ForceExitState = "idle" | "confirming" | "submitting" | "error";

function ForceExitButton({
  tradeId,
  onConfirm,
}: Readonly<{
  tradeId: string;
  onConfirm: (tradeId: string) => Promise<void>;
}>): ReactElement {
  const [state, setState] = useState<ForceExitState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const submit = async (): Promise<void> => {
    setState("submitting");
    try {
      await onConfirm(tradeId);
      setState("idle");
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : "unknown");
      setState("error");
    }
  };

  if (state === "idle")
    return (
      <ForceExitIdle
        onClick={() => {
          setState("confirming");
        }}
      />
    );
  if (state === "confirming")
    return (
      <ForceExitConfirming
        onCancel={() => {
          setState("idle");
        }}
        onConfirm={() => {
          void submit();
        }}
      />
    );
  if (state === "submitting") return <ForceExitSubmitting />;
  return (
    <ForceExitError
      message={errorMsg}
      onDismiss={() => {
        setErrorMsg(null);
        setState("idle");
      }}
    />
  );
}

function ForceExitIdle({ onClick }: Readonly<{ onClick: () => void }>): ReactElement {
  return (
    <button
      type="button"
      className="btn btn-secondary px-3 py-1 text-xs text-danger"
      onClick={onClick}
    >
      <i className="ti ti-alert-triangle text-xs" />
      Force exit
    </button>
  );
}

function ForceExitConfirming({
  onCancel,
  onConfirm,
}: Readonly<{ onCancel: () => void; onConfirm: () => void }>): ReactElement {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-ink-secondary">Sell now at market?</span>
      <button type="button" className="btn btn-secondary px-2 py-1 text-xs" onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className="btn bg-danger px-2 py-1 text-xs text-white hover:opacity-90"
        onClick={onConfirm}
      >
        Confirm exit
      </button>
    </div>
  );
}

function ForceExitSubmitting(): ReactElement {
  return (
    <span className="text-xs text-ink-secondary">
      <i className="ti ti-loader animate-spin text-xs" /> Exiting…
    </span>
  );
}

function ForceExitError({
  message,
  onDismiss,
}: Readonly<{ message: string | null; onDismiss: () => void }>): ReactElement {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-danger">Exit failed: {message ?? "unknown"}</span>
      <button type="button" className="btn btn-secondary px-2 py-1 text-xs" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

export function EmptyOpenPosition(): ReactElement {
  return (
    <section className="card flex items-center gap-3 p-4">
      <i className="ti ti-circle-dashed text-lg text-ink-muted" />
      <div>
        <div className="text-sm font-medium">No open position</div>
        <div className="text-xs text-ink-secondary">Waiting for the next entry signal.</div>
      </div>
    </section>
  );
}

function BiasBadge({ bias }: Readonly<{ bias: "CALL" | "PUT" | null }>): ReactElement {
  if (bias === "CALL") {
    return (
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-trending-up text-xs" /> CALL
      </span>
    );
  }
  if (bias === "PUT") {
    return (
      <span className="pill bg-danger-bg text-danger">
        <i className="ti ti-trending-down text-xs" /> PUT
      </span>
    );
  }
  return <span className="pill bg-surface-2 text-ink-muted">—</span>;
}

/** ITM-3 / ATM / OTM-1: how far in or out of the money the strike bought is. */
function RungBadge({ trade }: Readonly<{ trade: TradeLogEntry }>): ReactElement | null {
  const rung = rungOf(trade);
  if (rung === null) return null;
  return (
    <span
      className="pill bg-surface-2 text-ink-secondary"
      title={
        rung.derived ? "Worked out from the index level at entry" : "The strike the engine bought"
      }
    >
      {rung.derived ? `~${rung.label}` : rung.label}
    </span>
  );
}

function TrailBadge({ count }: Readonly<{ count: number }>): ReactElement {
  return (
    <span className="pill bg-accent-bg text-accent">
      <i className="ti ti-stairs-up text-sm" />
      Trail x{String(count)}
    </span>
  );
}

interface MiniStatProps {
  label: string;
  value: string;
  hint?: string | undefined;
}

function MiniStat({ label, value, hint }: Readonly<MiniStatProps>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-ink-secondary">{label}</div>
      <div className="mt-0.5 text-base font-medium">{value}</div>
      {hint !== undefined && <div className="mt-0.5 text-[11px] text-ink-muted">{hint}</div>}
    </div>
  );
}

interface ThresholdDistance {
  thresholdLabel: string;
  distanceLabel: string | undefined;
}

/** Next-ratchet threshold from the trade's REAL ladder parameters
 * (trail_trigger_points stamped at entry). entryPremium is fill-based on
 * the API side, so this matches the engine's fill-anchored math exactly.
 * Instrument defaults only cover legacy rows missing the stamped values. */
function computeNextThresholdDistance(trade: TradeLogEntry): ThresholdDistance {
  if (trade.entryPremium === null) {
    return { thresholdLabel: "—", distanceLabel: undefined };
  }
  const triggerPoints = trade.trailTriggerPoints ?? defaultTriggerPointsFor(trade.contractSymbol);
  const next = trade.entryPremium + (trade.trailCount + 1) * triggerPoints;
  if (trade.highWaterMark === null) {
    return { thresholdLabel: `Rs ${next.toFixed(2)}`, distanceLabel: undefined };
  }
  const gap = next - trade.highWaterMark;
  const distanceLabel = gap > 0 ? `Rs ${gap.toFixed(2)} to go` : "Due — engine catching up";
  return { thresholdLabel: `Rs ${next.toFixed(2)}`, distanceLabel };
}

/** Rungs 1..STANDARD_RUNGS gain the trail amount per rung; beyond that
 * the ladder goes 1:1 (trigger per rung). Mirrors the backend's
 * trail_ladder._STANDARD_RUNGS (2026-07-15 rework). */
const STANDARD_RUNGS = 5;

/** What the SL becomes once the next ratchet fires — the engine's
 * 2026-07-15 formula slip + Y*(min(n,5)-1) + X*max(n-5,0) anchored on the
 * fill, so the card matches the engine in BOTH the standard and 1:1
 * phases. Null slippage (pre-rework rows) reads as 0, exactly like the
 * engine. Legacy rows without an entry fall back to current SL + Y. */
function nextSlLabel(trade: TradeLogEntry): string {
  const next = nextSlValue(trade);
  return next === null ? "—" : `Rs ${next.toFixed(2)}`;
}

function nextSlValue(trade: TradeLogEntry): number | null {
  if (trade.currentSlPrice === null) return null;
  const amount = trade.trailAmountPoints ?? defaultAmountPointsFor(trade.contractSymbol);
  if (trade.entryPremium === null) {
    return trade.currentSlPrice + amount;
  }
  const trigger = trade.trailTriggerPoints ?? defaultTriggerPointsFor(trade.contractSymbol);
  const slippage = trade.trailSlippagePoints ?? 0;
  const nextRung = trade.trailCount + 1;
  const standardRungs = Math.min(nextRung, STANDARD_RUNGS);
  const ladder =
    slippage + amount * (standardRungs - 1) + trigger * Math.max(nextRung - STANDARD_RUNGS, 0);
  return trade.entryPremium + ladder;
}

function nextSlHint(trade: TradeLogEntry): string | undefined {
  if (trade.currentSlPrice === null) return undefined;
  return `after step ${String(trade.trailCount + 1)}`;
}

function defaultTriggerPointsFor(symbol: string | null): number {
  if (symbol?.startsWith("SENSEX")) return 22;
  // NIFTY (and unknown fallback) = 10 per design defaults.
  return 10;
}

function defaultAmountPointsFor(symbol: string | null): number {
  if (symbol?.startsWith("SENSEX")) return 23;
  // NIFTY (and unknown fallback) = 11 per design defaults.
  return 11;
}

/** Entry timestamp in IST (HH:MM:SS, 24h) — lets the operator line the
 * fill up against the TradingView signal time. entryAt is a UTC ISO
 * string; we render it in Asia/Kolkata regardless of browser locale. */
function formatEntryTime(entryAtIso: string): string {
  return new Date(entryAtIso).toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function lotsLabel(lots: number | null): string {
  if (lots === null) return "";
  return ` (${String(lots)} ${lots === 1 ? "lot" : "lots"})`;
}

function formatHeld(entryAtIso: string, now: Date): string {
  const ms = Math.max(0, now.getTime() - new Date(entryAtIso).getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${String(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h)}h ${String(m).padStart(2, "0")}m`;
}
